import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  filterAndPrioritizeIndustries,
  industriesForSectorCountry,
  normalizeCountry,
  sectorsForCountry,
} from "../lib/industryFilter";
import { fetchSectorIndustryList } from "../utils/sectorIndustryList";

const COUNTRY_OPTIONS = ["India", "United States"];

const IndustryContextPicker = ({
  value,
  onChange,
  inputStyle,
  selectStyle,
  labelStyle,
  formGroupStyle,
}) => {
  const [sectors, setSectors] = useState([]);
  const [flattened, setFlattened] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);

  const country = normalizeCountry(value?.country) || "";
  const sector = value?.sector || "";
  const industry = value?.industry || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const { sectors: list, flattened: flat } = await fetchSectorIndustryList();
        if (!cancelled) {
          setSectors(list);
          setFlattened(flat);
        }
      } catch (err) {
        console.error("Error fetching sector-industry list", err);
        if (!cancelled) {
          setLoadError("Failed to load industries. Check login and try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (industry && !searchText) {
      setSearchText(industry);
    }
  }, [industry, searchText]);

  const availableSectors = useMemo(
    () => sectorsForCountry(sectors, country),
    [sectors, country]
  );

  const availableIndustries = useMemo(() => {
    if (!sector) return [];
    return industriesForSectorCountry(sectors, sector, country);
  }, [sectors, sector, country]);

  const searchResults = useMemo(() => {
    const countryFiltered = country
      ? flattened.filter((t) => t.country === country)
      : flattened;
    return filterAndPrioritizeIndustries(countryFiltered, searchText, 25);
  }, [flattened, searchText, country]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const emit = (patch) => {
    onChange({ country, sector, industry, ...patch });
  };

  const handleCountryChange = (e) => {
    const nextCountry = normalizeCountry(e.target.value);
    emit({ country: nextCountry, sector: "", industry: "" });
    setSearchText("");
  };

  const handleSectorChange = (e) => {
    emit({ sector: e.target.value, industry: "" });
    setSearchText("");
  };

  const handleIndustryChange = (e) => {
    emit({ industry: e.target.value });
    setSearchText(e.target.value);
  };

  const handleSearchPick = (tuple) => {
    emit({
      country: tuple.country,
      sector: tuple.sector,
      industry: tuple.industry,
    });
    setSearchText(tuple.industry);
    setSearchOpen(false);
  };

  const handleReset = () => {
    onChange({ country: "", sector: "", industry: "" });
    setSearchText("");
    setSearchOpen(false);
  };

  const hasSelection = Boolean(sector || industry || searchText.trim() || country);

  const hintStyle = {
    fontSize: "13px",
    color: "#6b7280",
    marginBottom: "10px",
    lineHeight: 1.45,
  };

  const dropdownListStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 20,
    marginTop: "4px",
    maxHeight: "220px",
    overflowY: "auto",
    backgroundColor: "#fff",
    border: "1px solid #3730a3",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  };

  const dropdownItemStyle = {
    padding: "10px 12px",
    fontSize: "13px",
    cursor: "pointer",
    borderBottom: "1px solid #f3f4f6",
  };

  const summaryStyle = {
    marginTop: "10px",
    fontSize: "13px",
    color: "#374151",
    padding: "10px 12px",
    backgroundColor: "#f9fafb",
    borderRadius: "6px",
    border: "1px solid #e5e7eb",
  };

  const boxStyle = {
    border: "2px solid #3730a3",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "#fafbff",
  };

  const headerRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    marginBottom: "4px",
  };

  const resetButtonStyle = {
    flexShrink: 0,
    padding: "8px 14px",
    fontSize: "13px",
    backgroundColor: "#ffffff",
    color: "#3730a3",
    border: "1px solid #3730a3",
    borderRadius: "6px",
    cursor: hasSelection ? "pointer" : "not-allowed",
    fontWeight: "600",
    opacity: hasSelection ? 1 : 0.5,
  };

  return (
    <div style={formGroupStyle}>
      <div style={boxStyle}>
        <div style={headerRowStyle}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>
            Default industry context (Category mode){" "}
            <span style={{ fontSize: "12px", fontWeight: "500", color: "#6b7280" }}>
              Optional
            </span>
          </label>
          <button
            type="button"
            onClick={handleReset}
            disabled={!hasSelection || loading}
            style={resetButtonStyle}
            title="Clear country, sector, and industry defaults"
          >
            Reset Category
          </button>
        </div>
      <p style={hintStyle}>
        Sets template defaults for end users: sector, industry, and country saved as{" "}
        <code style={{ fontSize: "12px" }}>metadata.CATEGORIES</code> and{" "}
        <code style={{ fontSize: "12px" }}>metadata.stock_country</code>. Separate from
        Agent Category (library tab/filter above).
      </p>
      {loadError && <p style={{ ...hintStyle, color: "#ef4444" }}>{loadError}</p>}
      {loading && !loadError && <p style={hintStyle}>Loading industries…</p>}

      <div style={{ marginBottom: "12px", position: "relative" }} ref={searchRef}>
        <label style={{ ...labelStyle, fontSize: "13px" }}>Search industry</label>
        <input
          type="text"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setSearchOpen(true);
          }}
          onFocus={() => setSearchOpen(true)}
          placeholder="Type to search by industry, sector, or country"
          style={inputStyle}
          disabled={loading || Boolean(loadError)}
        />
        {searchOpen && searchText.trim() && searchResults.length > 0 && (
          <div style={dropdownListStyle}>
            {searchResults.map((tuple) => (
              <div
                key={`${tuple.industry}::${tuple.sector}::${tuple.country}`}
                style={dropdownItemStyle}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSearchPick(tuple)}
              >
                <strong>{tuple.industry}</strong>
                <span style={{ color: "#6b7280" }}>
                  {" "}
                  — {tuple.sector}, {tuple.country}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
        <div>
          <label style={{ ...labelStyle, fontSize: "13px" }}>Country</label>
          <select
            value={country}
            onChange={handleCountryChange}
            style={selectStyle}
            disabled={loading || Boolean(loadError)}
          >
            <option value="">Select country</option>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ ...labelStyle, fontSize: "13px" }}>Sector</label>
          <select
            value={sector}
            onChange={handleSectorChange}
            style={selectStyle}
            disabled={loading || Boolean(loadError) || !country || availableSectors.length === 0}
          >
            <option value="">Select sector</option>
            {availableSectors.map((s) => (
              <option key={s.name} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ ...labelStyle, fontSize: "13px" }}>Industry</label>
          <select
            value={industry}
            onChange={handleIndustryChange}
            style={selectStyle}
            disabled={loading || Boolean(loadError) || !sector}
          >
            <option value="">Select industry</option>
            {availableIndustries.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {industry && sector && country && (
        <div style={summaryStyle}>
          Saved as: <strong>{country}</strong> → <strong>{sector}</strong> →{" "}
          <strong>{industry}</strong> (CATEGORIES: [&quot;{industry}&quot;])
        </div>
      )}
      </div>
    </div>
  );
};

export default IndustryContextPicker;
